import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { useMe } from '../../lib/auth.tsx';
import { useClientGrid } from '../../lib/grid.ts';
import { KIND, dday, type TodoItem, type Todos } from '../../lib/schedule.ts';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { Badge } from '../../components/ui/Button.tsx';
import { can, wide } from '../../lib/perm.ts';


/** 통합 할 일: 내 할 일 / 부서 할 일 (계산 결과, 클라이언트 그리드) */
export function TodosPage() {
  const me = useMe();
  const [scope, setScope] = useState<'mine' | 'dept'>(new URLSearchParams(window.location.search).get('scope') === 'dept' ? 'dept' : 'mine');
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ['todos', scope, days], queryFn: () => api<Todos>(`/schedule/todos/${scope}?days=${days}`) });
  const columns = useMemo<Column<TodoItem>[]>(() => [
    { key: 'kind', label: '종류', width: '8rem', filter: 'select', options: Object.entries(KIND).map(([value, k]) => ({ value, label: k.label })), render: (t) => <Badge tone={KIND[t.kind]?.tone ?? 'neutral'}>{t.kindLabel}</Badge> },
    { key: 'title', label: '항목', wrap: true, filter: 'text', render: (t) => <Link className="text-accent-strong hover:underline" to={t.url}>{t.title}</Link> },
    { key: 'dueDate', label: '기한', width: '6.5rem', filter: 'date', day: true, render: (t) => t.dueDate ?? '—' },
    { key: 'days', label: 'D-day', width: '6rem', align: 'right', filter: 'none', render: (t) => <span className={t.days != null && t.days < 0 ? 'text-red-700 font-semibold' : t.days === 0 ? 'text-amber-700 font-semibold' : ''}>{dday(t.days)}</span> },
    { key: 'status', label: '상태', width: '6rem', filter: 'select', render: (t) => t.status === 'overdue' ? <Badge tone="danger">지연</Badge> : t.status },
  ], []);
  const grid = useClientGrid(q.data?.items, columns, { pageSize: 50, sort: { col: 'days', dir: 'asc' } });
  const c = q.data?.counts;
  return (
    <div>
      <PageHeader title="할 일" />
      <div className="flex items-center gap-2 mb-2 text-[12px]">
        <div className="flex border border-slate-300">{(['mine', 'dept'] as const).filter((s) => s === 'mine' || (can(me.data, 'schedule.manage') || wide(me.data, 'schedule.view'))).map((s) => <button key={s} type="button" className={`px-2.5 py-1 ${scope === s ? 'bg-accent text-white' : 'bg-white text-slate-600'}`} onClick={() => setScope(s)}>{s === 'mine' ? '내 할 일' : '부서 할 일'}</button>)}</div>
        <select className="h-6 border border-slate-300 px-1" value={days} onChange={(e) => setDays(Number(e.target.value))}>{[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d}일 이내</option>)}</select>
        {c && <span className="text-slate-600 ml-2">전체 <b>{c.total}</b> · 지연 <b className={c.overdue ? 'text-red-700' : ''}>{c.overdue}</b> · 오늘 <b className={c.today ? 'text-amber-700' : ''}>{c.today}</b> · 7일 내 <b>{c.week}</b></span>}
      </div>
      {q.isError && <div className="text-red-600 text-sm mb-2">{(q.error as Error).message}</div>}
      <DataTable columns={columns} data={grid.data} state={grid.state} onChange={grid.set} rowKey={(t) => `${t.kind}:${t.id}`} isFetching={q.isFetching} compact pagination="auto" />
    </div>
  );
}
